import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { Action, Actor, enrollmentTracking } from '../Integrations/Tracking'
import { ComparatorFactory } from '../Utils/comparison'
import { isVmeConfirmation } from '../Utils/questions'
import {
    AdvisedTechnique,
    Condition,
    GeneralQuestions,
    Operator,
    Question,
    QuestionsData,
    RenovationCategory,
    Response,
    TechniqueQuestions,
} from '../global'
import { client } from '../http'

export const useQuestionStore = defineStore('question', () => {
    const questionsData = ref<QuestionsData>({} as QuestionsData)
    const selectedTechniques = ref<string[]>([])
    const error = ref<string>()
    const generalQuestionsArePrefilled = ref<boolean>(false)
    // Kept apart from the questions, as those are reloaded once the postal code is known.
    const vmeConfirmations = ref<Record<string, boolean>>({})

    const generalQuestions = computed<GeneralQuestions>(() => questionsData.value.general || {})
    const techniquesQuestions = computed<TechniqueQuestions[]>(
        () => questionsData.value.techniques || []
    )
    const categories = computed<RenovationCategory[]>(
        () => questionsData.value.renovationCategories || []
    )

    const selectedTechniquesQuestions = computed<TechniqueQuestions[]>(() =>
        techniquesQuestions.value.filter(({ techniqueId }) =>
            selectedTechniques.value.includes(techniqueId)
        )
    )

    const techniques = computed(() =>
        techniquesQuestions.value.map(({ techniqueId, key, name }) => ({
            id: techniqueId,
            key,
            name,
        }))
    )
    const techniquesToBeDisplayed = computed<string[]>(() => {
        return techniquesQuestions.value
            .filter(
                ({ techniqueId, questions }) =>
                    selectedTechniques.value.includes(techniqueId) &&
                    questions.some(({ type }) => type === 'technique')
            )
            .map(({ techniqueId }) => techniqueId)
    })

    function apartmentConfirmationFor(techniqueId: string): Question | undefined {
        return techniquesQuestions.value
            .find((technique) => technique.techniqueId === techniqueId)
            ?.questions.find(isVmeConfirmation)
    }

    function apartmentConfirmationAnswers(techniqueIds: string[]): Question[] {
        return techniquesQuestions.value.flatMap(({ techniqueId }) => {
            const question = apartmentConfirmationFor(techniqueId)

            if (!question) return []

            const answer =
                techniqueIds.includes(techniqueId) && vmeConfirmations.value[question.questionId]
                    ? question.selectOptions?.[0]?.answerId
                    : undefined

            return [{ ...question, answer }]
        })
    }

    function initialAnswerForQuestion(question: Question): string | string[] | undefined {
        const questions = techniquesQuestions.value.flatMap(({ questions }) => questions)
        return questions.find(
            ({ questionId, answer }) => questionId === question.questionId && !!answer
        )?.answer
    }

    async function getQuestions(campaignId: string, postalCode?: string): Promise<void> {
        try {
            let url = `/campaigns/${campaignId}/retrieve-questions`

            if (postalCode) {
                url += `?postalCode=${encodeURIComponent(postalCode)}`
            }

            const { data } = await client.get<QuestionsData>(url)

            if (!questionsData.value.general) {
                questionsData.value.general = data.general
            }
            questionsData.value.techniques = data.techniques
            questionsData.value.renovationCategories = data.renovationCategories

            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }
    }

    function adviseTechniques(advisedTechniques: AdvisedTechnique[]) {
        const availableTechniques = techniques.value.map((technique) => technique.id)

        enrollmentTracking.track(Actor.Homeowner, Action.AdviseTechniques)

        selectedTechniques.value = advisedTechniques
            .map((advisedTechnique) => advisedTechnique.technique.uuid)
            .filter((techniqueId) => availableTechniques.includes(techniqueId))
    }

    function applyQuestionConditions(questions: Question[]): Question[] {
        const shouldDisplay = (questionId: string, condition: Condition) => {
            const answer = questions.find((question) => question.questionId === questionId)?.answer

            if (!answer) {
                return false
            }

            const [operator, answerId] = Object.entries(condition)[0]
            const comparator = ComparatorFactory.create(operator as Operator)

            return comparator.compare(answer, answerId)
        }

        return questions.filter(({ configuration }) => {
            if (!configuration?.displayWhen) {
                return true
            }

            return Object.entries(configuration.displayWhen).every(
                ([operatorOrQuestionId, conditions]) => {
                    if (operatorOrQuestionId === 'and') {
                        return Object.entries(conditions as Record<string, Condition>).every(
                            ([questionId, condition]) => shouldDisplay(questionId, condition)
                        )
                    }

                    if (operatorOrQuestionId === 'or') {
                        return Object.entries(conditions as Record<string, Condition>).some(
                            ([questionId, condition]) => shouldDisplay(questionId, condition)
                        )
                    }

                    return shouldDisplay(operatorOrQuestionId, conditions as Condition)
                }
            )
        })
    }

    function fillAnswers(responses: Response[]): void {
        responses.forEach((response: Response) => {
            // Only set for general questions for now
            const questionIndex = questionsData.value.general.questions.findIndex(
                ({ questionId }) => questionId === response.questionId
            )

            if (questionIndex !== undefined) {
                questionsData.value.general.questions[questionIndex].answer = response.answer
            }
        })

        generalQuestionsArePrefilled.value = generalQuestions.value.questions.every(
            (question) => question.answer !== undefined
        )
    }

    return {
        selectedTechniques,
        error,
        generalQuestions,
        techniquesQuestions,
        categories,
        selectedTechniquesQuestions,
        techniques,
        techniquesToBeDisplayed,
        generalQuestionsArePrefilled,
        vmeConfirmations,
        initialAnswerForQuestion,
        apartmentConfirmationFor,
        apartmentConfirmationAnswers,
        getQuestions,
        adviseTechniques,
        applyQuestionConditions,
        fillAnswers,
    }
})
